/** @format */

import {createContext, ReactNode, useContext, useMemo} from 'react';

import {endOfMonth, format, startOfMonth, subDays, subMonths} from 'date-fns';

import {ApolloError} from '@apollo/client';

import {
  GetBudgetWidgetsDataQueryPage,
  useGetBudgetWidgetsDataQuery,
} from '../graphql/__generated__';

export interface BudgetWidgetsDataContextValue {
  data?: GetBudgetWidgetsDataQueryPage;
  loading: boolean;
  error?: ApolloError;
}

const initialValue: BudgetWidgetsDataContextValue = {
  loading: true,
};

interface ProviderProps {
  children: ReactNode;
}

const BudgetWidgetsDataContext = createContext<BudgetWidgetsDataContextValue>(initialValue);

export const BudgetWidgetsDataProvider = ({children}: ProviderProps) => {
  const {
    yesterday,
    dayBeforeYesterday,
    monthStart,
    monthEnd,
    prevMonthStart,
    prevMonthEnd,
    currentMonthStart,
    currentMonthEnd,
    previousMonthStart,
    previousMonthEnd,
  } = useMemo(() => {
    const todayDate = new Date();
    const todayString = format(todayDate, 'yyyy-MM-dd');
    const normalizedToday = new Date(todayString);
    const yesterdayDate = subDays(normalizedToday, 1);
    const dayBeforeYesterdayDate = subDays(normalizedToday, 2);

    const currentMonthStartDate = startOfMonth(yesterdayDate);
    const currentMonthEndDate = endOfMonth(todayDate);

    const previousMonthDate = subMonths(yesterdayDate, 1);
    const previousMonthStartDate = startOfMonth(previousMonthDate);
    const previousMonthEndDate = endOfMonth(previousMonthDate);

    return {
      yesterday: yesterdayDate,
      dayBeforeYesterday: dayBeforeYesterdayDate,
      monthStart: currentMonthStartDate,
      monthEnd: currentMonthEndDate,
      prevMonthStart: previousMonthStartDate,
      prevMonthEnd: previousMonthEndDate,
      currentMonthStart: currentMonthStartDate,
      currentMonthEnd: currentMonthEndDate,
      previousMonthStart: previousMonthStartDate,
      previousMonthEnd: previousMonthEndDate,
    };
  }, []);

  const {
    data: budgetWidgetsData,
    loading: budgetWidgetsLoading,
    error: budgetWidgetsError,
  } = useGetBudgetWidgetsDataQuery({
    variables: {
      yesterdayStart: format(yesterday, 'yyyy-MM-dd'),
      yesterdayEnd: format(yesterday, 'yyyy-MM-dd'),
      dayBeforeYesterdayStart: format(dayBeforeYesterday, 'yyyy-MM-dd'),
      dayBeforeYesterdayEnd: format(dayBeforeYesterday, 'yyyy-MM-dd'),
      monthStart: format(monthStart, 'yyyy-MM-dd'),
      monthEnd: format(monthEnd, 'yyyy-MM-dd'),
      prevMonthStart: format(prevMonthStart, 'yyyy-MM-dd'),
      prevMonthEnd: format(prevMonthEnd, 'yyyy-MM-dd'),
      currentMonthStart: format(currentMonthStart, 'yyyy-MM-dd'),
      currentMonthEnd: format(currentMonthEnd, 'yyyy-MM-dd'),
      previousMonthStart: format(previousMonthStart, 'yyyy-MM-dd'),
      previousMonthEnd: format(previousMonthEnd, 'yyyy-MM-dd'),
    },
  });

  const contextValue = useMemo(
    () => ({
      data: budgetWidgetsData,
      loading: budgetWidgetsLoading,
      error: budgetWidgetsError,
    }),
    [budgetWidgetsData, budgetWidgetsError, budgetWidgetsLoading],
  );

  return (
    <BudgetWidgetsDataContext.Provider value={contextValue}>
      {children}
    </BudgetWidgetsDataContext.Provider>
  );
};

export const useBudgetWidgetsData = () => useContext(BudgetWidgetsDataContext);
